import React, { useMemo, useState } from 'react';
import { AtSign, ChevronDown, CircleCheck, ExternalLink, Globe, Newspaper, Radio } from 'lucide-react';
import { Card, EmptyState, Pill, SegmentedControl } from '../ui';
import type {
  NewsCategory,
  NewsFeed as NewsFeedData,
  NewsItem,
  NewsReach,
  NewsSignificance,
  NewsSourceType,
  NewsTone,
} from '../../types/news';
import { MetaLine, SentimentMark, SeverityPill } from '../../features/monitoring/components/ui';
import { formatDate, hostOf, sentimentBand, SENTIMENT_LABEL } from '../../features/monitoring/format';
import { ConfidenceBadge } from '../../features/intelligence/components/Confidence';

/* ------------------------------------------------------------------ */
/* Labels                                                              */
/* ------------------------------------------------------------------ */

export const NEWS_CATEGORY_LABEL: Record<NewsCategory, string> = {
  appointment: 'Appointment',
  departure: 'Departure',
  legal: 'Legal',
  regulatory: 'Regulatory',
  protest: 'Protest',
  financial: 'Financial',
  corruption: 'Corruption',
  partnership: 'Partnership',
  statement: 'Statement',
  security: 'Security',
  profile: 'Profile',
  other: 'Other',
};

const TONE_LABEL: Record<NewsTone, string> = {
  favourable: 'Favourable',
  mixed: 'Mixed',
  neutral: 'Neutral',
  hostile: 'Hostile',
  none: 'No coverage',
};

export const NEWS_REACH_LABEL: Record<NewsReach, string> = {
  local: 'Local',
  national: 'National',
  regional: 'Regional',
  international: 'International',
};

export const NEWS_SOURCE_TYPE_LABEL: Record<NewsSourceType, string> = {
  news: 'Press',
  social: 'Social',
  official: 'Official',
  broadcast: 'Broadcast',
  academic: 'Academic',
  other: 'Other',
};

/** Most distant first, so the reach filter reads as a widening circle */
const REACH_ORDER: NewsReach[] = ['international', 'regional', 'national', 'local'];

const toneTone = (tone: NewsTone) =>
  tone === 'hostile' ? 'red' : tone === 'favourable' ? 'green' : tone === 'mixed' ? 'amber' : 'neutral';

type Importance = 'all' | 'important';

const IMPORTANT: NewsSignificance[] = ['critical', 'high'];

/** "September 2026" for grouping; undated items share one group at the end */
const monthKey = (iso: string | null): string => (iso ? iso.slice(0, 7) : 'undated');
const monthLabel = (key: string, locale?: string): string => {
  if (key === 'undated') return 'Undated';
  const [year, month] = key.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString(locale, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

/* ------------------------------------------------------------------ */
/* Overview                                                            */
/* ------------------------------------------------------------------ */

const Overview: React.FC<{ feed: NewsFeedData }> = ({ feed }) => {
  const { overview, stats, items } = feed;
  const sentiment = stats.sentiment;
  const period =
    stats.from && stats.to
      ? stats.from === stats.to
        ? formatDate(stats.from)
        : `${formatDate(stats.from)} – ${formatDate(stats.to)}`
      : null;

  return (
    <section aria-label="Coverage overview" className="space-y-3">
      {overview.summary && <p className="text-[14px] leading-relaxed text-neutral-700">{overview.summary}</p>}
      {overview.trajectory && <p className="text-[13px] text-neutral-500">{overview.trajectory}</p>}

      <MetaLine
        className="text-[12px]"
        parts={[
          `${items.length} item${items.length === 1 ? '' : 's'}`,
          stats.sources ? `${stats.sources} outlet${stats.sources === 1 ? '' : 's'}` : null,
          period,
          stats.countries?.length ? `${stats.countries.length} countries` : null,
          stats.fromMonitoring ? `${stats.fromMonitoring} from monitoring` : null,
        ]}
      />

      {(overview.themes.length > 0 || overview.tone !== 'neutral' || sentiment !== null) && (
        <div className="flex flex-wrap items-center gap-1.5">
          <Pill tone={toneTone(overview.tone)} title="How the coverage reflects on the person overall">
            {TONE_LABEL[overview.tone]}
          </Pill>
          {sentiment !== null && (
            <Pill tone="neutral" title={`Mean sentiment ${sentiment > 0 ? '+' : ''}${sentiment.toFixed(2)}`}>
              {SENTIMENT_LABEL[sentimentBand(sentiment)]} on average
            </Pill>
          )}
          {overview.themes.map((theme) => (
            <Pill key={theme} tone="outline">
              {theme}
            </Pill>
          ))}
        </div>
      )}

      {(overview.international || overview.socialPresence) && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {overview.international && (
            <div className="rounded-2xl border border-neutral-200 px-4 py-3">
              <p className="mb-1 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.02em] text-neutral-400">
                <Globe className="h-3.5 w-3.5" strokeWidth={1.75} />
                International
              </p>
              <p className="text-[13px] leading-relaxed text-neutral-700">{overview.international}</p>
            </div>
          )}
          {overview.socialPresence && (
            <div className="rounded-2xl border border-neutral-200 px-4 py-3">
              <p className="mb-1 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.02em] text-neutral-400">
                <AtSign className="h-3.5 w-3.5" strokeWidth={1.75} />
                Social presence
              </p>
              <p className="text-[13px] leading-relaxed text-neutral-700">{overview.socialPresence}</p>
            </div>
          )}
        </div>
      )}

      {overview.watch.length > 0 && (
        <div className="rounded-2xl border border-neutral-200 px-4 py-3">
          <p className="mb-1.5 text-[11px] uppercase tracking-[0.02em] text-neutral-400">Watch</p>
          <ul className="space-y-1">
            {overview.watch.map((line, index) => (
              <li key={index} className="flex gap-2 text-[13px] leading-snug text-neutral-700">
                <span aria-hidden="true" className="mt-[7px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-neutral-300" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* One item                                                            */
/* ------------------------------------------------------------------ */

const NewsRow: React.FC<{ item: NewsItem; locale?: string }> = ({ item, locale }) => {
  const [open, setOpen] = useState(false);
  const host = hostOf(item.url);
  const hasMore = !!(item.whyItMatters || item.quote || item.mentions.length);

  return (
    <article className="px-4 py-3" data-testid="news-item">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <SeverityPill severity={item.significance} />
        <Pill tone="outline">{NEWS_CATEGORY_LABEL[item.category]}</Pill>
        {item.sourceType !== 'news' && (
          <Pill tone="violet" title={`${NEWS_SOURCE_TYPE_LABEL[item.sourceType]} source`}>
            {NEWS_SOURCE_TYPE_LABEL[item.sourceType]}
          </Pill>
        )}
        {item.reach === 'international' && (
          <Pill tone="teal" title="Carried beyond the region">
            <Globe className="mr-1 h-3 w-3" strokeWidth={1.75} />
            International
          </Pill>
        )}
        {item.origin === 'monitoring' && (
          <Pill tone="blue" title="Found earlier by monitoring">
            <Radio className="mr-1 h-3 w-3" strokeWidth={1.75} />
            Monitoring
          </Pill>
        )}
        <span className="ml-auto text-[12px] tabular-nums text-neutral-400" title={item.publishedAt ?? undefined}>
          {item.publishedAt ? formatDate(item.publishedAt) : 'Undated'}
        </span>
      </div>

      {item.url ? (
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1.5 inline-flex items-start gap-1 text-[14px] leading-snug text-ink hover:underline hover:decoration-neutral-300 hover:underline-offset-2"
        >
          <span className="min-w-0">{item.title}</span>
          <ExternalLink className="mt-1 h-3 w-3 flex-shrink-0 text-neutral-400" strokeWidth={1.75} />
        </a>
      ) : (
        <p className="mt-1.5 text-[14px] leading-snug text-ink">{item.title}</p>
      )}

      <p className={`mt-1 text-[13px] leading-relaxed text-neutral-600 ${open ? '' : 'line-clamp-3'}`}>{item.summary}</p>

      {open && item.whyItMatters && (
        <p className="mt-2 text-[13px] leading-relaxed text-ink">
          <span className="mr-1.5 text-[11px] uppercase tracking-[0.02em] text-neutral-400">Why it matters</span>
          {item.whyItMatters}
        </p>
      )}
      {open && item.quote && (
        <blockquote className="mt-2 border-l-2 border-neutral-200 pl-3 text-[13px] italic leading-relaxed text-neutral-500">
          {item.quote}
        </blockquote>
      )}
      {open && item.mentions.length > 0 && (
        <p className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] uppercase tracking-[0.02em] text-neutral-400">Mentions</span>
          {item.mentions.map((name) => (
            <Pill key={name} tone="outline">
              {name}
            </Pill>
          ))}
        </p>
      )}

      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
        <MetaLine
          parts={[
            item.source,
            host && host !== item.source ? host : null,
            item.country,
            item.language ? item.language.toUpperCase() : null,
            item.reach !== 'international' ? NEWS_REACH_LABEL[item.reach] : null,
          ]}
        />
        <SentimentMark value={item.sentiment} />
        <ConfidenceBadge value={item.confidence * 100} showValue={false} />
        {item.url && item.verification === 'live' && (
          <span className="inline-flex items-center gap-1 text-[12px] text-neutral-400" title="The link was reachable when the feed was built">
            <CircleCheck className="h-3.5 w-3.5 text-emerald-600" strokeWidth={1.75} />
            Link verified
          </span>
        )}
        {hasMore && (
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
            className="ml-auto inline-flex h-7 items-center gap-1 rounded-full px-2 text-[12px] text-neutral-500 transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
          >
            {open ? 'Less' : 'More'}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} strokeWidth={1.75} />
          </button>
        )}
      </div>
    </article>
  );
};

/* ------------------------------------------------------------------ */
/* The feed                                                            */
/* ------------------------------------------------------------------ */

export interface NewsFeedProps {
  feed: NewsFeedData;
  className?: string;
}

/**
 * A structured news feed: the analytical overview, then the items grouped by
 * month with category and importance filters. Rendered from the JSON the
 * server verified and stored — nothing here is model-written HTML.
 */
export const NewsFeed: React.FC<NewsFeedProps> = ({ feed, className = '' }) => {
  const [category, setCategory] = useState<NewsCategory | 'all'>('all');
  const [reach, setReach] = useState<NewsReach | 'all'>('all');
  const [importance, setImportance] = useState<Importance>('all');
  const locale = feed.language === 'fr' ? 'fr-FR' : undefined;

  const categories = useMemo(() => {
    const counts = new Map<NewsCategory, number>();
    for (const item of feed.items) counts.set(item.category, (counts.get(item.category) ?? 0) + 1);
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [feed.items]);

  const reaches = useMemo(() => {
    const counts = new Map<NewsReach, number>();
    for (const item of feed.items) counts.set(item.reach, (counts.get(item.reach) ?? 0) + 1);
    return REACH_ORDER.filter((key) => counts.has(key)).map((key) => [key, counts.get(key)!] as const);
  }, [feed.items]);

  const visible = useMemo(
    () =>
      feed.items.filter(
        (item) =>
          (category === 'all' || item.category === category) &&
          (reach === 'all' || item.reach === reach) &&
          (importance === 'all' || IMPORTANT.includes(item.significance)),
      ),
    [feed.items, category, reach, importance],
  );

  const groups = useMemo(() => {
    const byMonth = new Map<string, NewsItem[]>();
    // Newest first inside a month, whatever order the feed was stored in
    const ordered = [...visible].sort((a, b) => (b.publishedAt ?? '').localeCompare(a.publishedAt ?? ''));
    for (const item of ordered) {
      const key = monthKey(item.publishedAt);
      const list = byMonth.get(key) ?? [];
      list.push(item);
      byMonth.set(key, list);
    }
    // Newest month first; undated last
    return Array.from(byMonth.entries()).sort(([a], [b]) => (a === 'undated' ? 1 : b === 'undated' ? -1 : a < b ? 1 : -1));
  }, [visible]);

  const importantCount = feed.items.filter((item) => IMPORTANT.includes(item.significance)).length;

  return (
    <div className={`space-y-5 ${className}`}>
      <Overview feed={feed} />

      {feed.items.length === 0 ? (
        <Card>
          <EmptyState icon={Newspaper} title="No coverage found" />
        </Card>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            {importantCount > 0 && importantCount < feed.items.length && (
              <SegmentedControl<Importance>
                label="Importance"
                value={importance}
                onChange={setImportance}
                items={[
                  { value: 'all', label: 'All' },
                  { value: 'important', label: `Important ${importantCount}` },
                ]}
              />
            )}
            {reaches.length > 1 && (
              <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Reach">
                <FilterChip active={reach === 'all'} onClick={() => setReach('all')}>
                  Everywhere
                </FilterChip>
                {reaches.map(([key, count]) => (
                  <FilterChip key={key} active={reach === key} onClick={() => setReach(key)}>
                    {NEWS_REACH_LABEL[key]} {count}
                  </FilterChip>
                ))}
              </div>
            )}
          </div>

          {categories.length > 1 && (
            <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Category">
              <FilterChip active={category === 'all'} onClick={() => setCategory('all')}>
                All {feed.items.length}
              </FilterChip>
              {categories.map(([key, count]) => (
                <FilterChip key={key} active={category === key} onClick={() => setCategory(key)}>
                  {NEWS_CATEGORY_LABEL[key]} {count}
                </FilterChip>
              ))}
            </div>
          )}

          {visible.length === 0 ? (
            <Card>
              <EmptyState icon={Newspaper} title="Nothing matches" />
            </Card>
          ) : (
            <div className="space-y-4">
              {groups.map(([key, items]) => (
                <section key={key} aria-label={monthLabel(key, locale)}>
                  <h3 className="mb-2 px-1 text-[12px] uppercase tracking-[0.02em] text-neutral-400">
                    {monthLabel(key, locale)}
                    <span className="ml-1.5 tabular-nums">{items.length}</span>
                  </h3>
                  <Card divided>
                    {items.map((item) => (
                      <NewsRow key={item.id} item={item} locale={locale} />
                    ))}
                  </Card>
                </section>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const FilterChip: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({
  active,
  onClick,
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`inline-flex h-7 items-center whitespace-nowrap rounded-full border px-2.5 text-[12px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 ${
      active ? 'border-ink bg-ink text-white' : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300 hover:text-ink'
    }`}
  >
    {children}
  </button>
);
